// -------------------------------------------------------------------
// Cloudinary Upload — signed uploads for HDR files
// Uses raw/upload endpoint since .hdr files aren't standard images
// -------------------------------------------------------------------

const CLOUD_NAME = 'dypt1fmsb';
const API_KEY = '652182827596455';
const API_SECRET = 'ZGOWCxY0bFA93ScPkoM5GbNQGeo';
const UPLOAD_FOLDER = 'hdr';

// SHA-1 hash (Web Crypto API)
async function sha1(message: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export interface CloudinaryUploadResult {
    secure_url: string;
    public_id: string;
    original_filename: string;
    bytes: number;
}

/**
 * Upload an HDR file to Cloudinary (signed upload).
 * Returns the secure URL and public ID.
 */
export async function uploadHDRToCloudinary(
    file: File,
    onProgress?: (percent: number) => void
): Promise<CloudinaryUploadResult> {
    const timestamp = Math.floor(Date.now() / 1000);

    // Build signature string (params in alphabetical order)
    const signatureString = `folder=${UPLOAD_FOLDER}&timestamp=${timestamp}${API_SECRET}`;
    const signature = await sha1(signatureString);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', API_KEY);
    formData.append('timestamp', String(timestamp));
    formData.append('signature', signature);
    formData.append('folder', UPLOAD_FOLDER);

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/upload`);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                const data = JSON.parse(xhr.responseText);
                resolve({
                    secure_url: data.secure_url,
                    public_id: data.public_id,
                    original_filename: data.original_filename,
                    bytes: data.bytes,
                });
            } else {
                reject(new Error(`Cloudinary upload failed: ${xhr.status} ${xhr.responseText}`));
            }
        };

        xhr.onerror = () => reject(new Error('Cloudinary upload network error'));
        xhr.send(formData);
    });
}

/**
 * Delete a file from Cloudinary by public_id (signed request).
 */
export async function deleteFromCloudinary(publicId: string): Promise<void> {
    const timestamp = Math.floor(Date.now() / 1000);
    const signatureString = `public_id=${publicId}&timestamp=${timestamp}${API_SECRET}`;
    const signature = await sha1(signatureString);

    const formData = new FormData();
    formData.append('public_id', publicId);
    formData.append('api_key', API_KEY);
    formData.append('timestamp', String(timestamp));
    formData.append('signature', signature);

    const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/destroy`,
        { method: 'POST', body: formData }
    );

    if (!res.ok) {
        console.warn('[Cloudinary] Delete failed:', await res.text());
    }
}

// -------------------------------------------------------------------
// List HDR files from Cloudinary folder via Edge Function proxy
// -------------------------------------------------------------------

export interface CloudinaryFile {
    public_id: string;
    secure_url: string;
    display_name: string;
    format: string;
    bytes: number;
    created_at: string;
}

/**
 * Fetch the list of HDR files from the Cloudinary "hdr" folder
 * via the Supabase Edge Function proxy (avoids CORS issues with Admin API).
 */
export async function listCloudinaryHDRs(): Promise<CloudinaryFile[]> {
    const res = await fetch(
        'https://xtxmiyuigxnuasxhpcnh.supabase.co/functions/v1/list-cloudinary-hdrs'
    );
    if (!res.ok) throw new Error(`Failed to list Cloudinary HDRs: ${res.status}`);
    const data = await res.json();
    return data.files || [];
}
